export type TipoContratoSplit = 'composicion';

export type EstadoContratoSplit = 
  | 'BORRADOR' 
  | 'EN_REVISION' 
  | 'DESCARGADO'
  | 'CON_FIRMAS' 
  | 'CERRADO';

export interface CambioCampoSplit {
  campo: string;
  etiqueta: string;
  antes: string;
  despues: string;
  sujeto?: string;
}

export interface ArchivoFirmadoMetadata {
  id?: string;
  nombreArchivo: string;
  tipoMime: string;
  tamanoBytes: number;
  fechaSubida: string;
  subidoPor?: string;
  dataBase64?: string;
  url?: string;
  storageKey?: string;
  pendienteSubidaS3?: boolean;
  idArchivoCola?: string;
  versionContrato?: number;
  esHistorico?: boolean;
  motivoDesactualizacion?: string;
  cambios?: CambioCampoSplit[];
}

export type TipoDocumentoIdentidad = 
  | 'CC' 
  | 'TI'
  | 'CE' 
  | 'PASAPORTE' 
  | 'NIT';

export interface EstudioEpgCatalogoItem {
  id: number;
  nombre: string;
  direccion?: string;
}

export interface TipoDocumentoCatalogoItem {
  id: number;
  codigo: TipoDocumentoIdentidad;
  etiqueta: string;
}

export interface CatalogosSplitsMml {
  estudios: EstudioEpgCatalogoItem[];
  tiposDocumento: TipoDocumentoCatalogoItem[];
}

/**
 * Únicos roles permitidos para el Split Sheet de Medellín Music Lab.
 * No agregar ni quitar palabras.
 */
export type RolContribucionAutoral = 
  | 'Autor' 
  | 'Compositor' 
  | 'Productor musical' 
  | 'Músico';

export const ROLES_SPLIT_OFICIALES: readonly RolContribucionAutoral[] = [
  'Autor',
  'Compositor',
  'Productor musical',
  'Músico'
] as const;

export const ESTUDIOS_EPG_MML: readonly string[] = [
  'EPG Sede Principal · Medellín Music Lab',
  'Ciudadela C4TA'
] as const;

export const TIPOS_DOCUMENTO_CATALOGO_DEFAULT: readonly TipoDocumentoCatalogoItem[] = [
  { id: 1, codigo: 'CC', etiqueta: 'Cédula de Ciudadanía (CC)' },
  { id: 2, codigo: 'TI', etiqueta: 'Tarjeta de Identidad (TI)' },
  { id: 3, codigo: 'CE', etiqueta: 'Cédula de Extranjería (CE)' },
  { id: 4, codigo: 'PASAPORTE', etiqueta: 'Pasaporte' },
  { id: 99, codigo: 'NIT', etiqueta: 'NIT (Persona Jurídica)' }
] as const;

export type RespuestaSiNo = 'SI' | 'NO';
export type RespuestaAutorizacionFijacion = 'SI' | 'NO' | 'NO_APLICA';

export type ProductorFonograficoSeleccion = 'PARTICIPANTE_SESION' | 'COPRODUCTORES';
export type ProductorFonograficoModalidad = 'PRODUCTOR_UNICO' | 'COPRODUCTORES';

export interface FirmaElectronicaRegistro {
  firmado: boolean;
  aceptacionExpresa: boolean;
  tipoFirma: 'TRAZO' | 'TEXTO';
  firmaDataUrl?: string;
  firmaTexto?: string;
  fechaFirma?: string;
  hashVerificacion?: string;
  trazabilidadTexto?: string;
}

export interface CoproductorFonograficoItem {
  id: string;
  colaboradorId?: string;
  nombreRazonSocial: string;
  tipoDocumento: TipoDocumentoIdentidad;
  numeroDocumento: string;
  correoAceptacion: string;
  porcentajeParticipacion: number;
}

export interface DesignacionProductorFonografico {
  seleccion: ProductorFonograficoSeleccion;
  modalidad: ProductorFonograficoModalidad;
  colaboradorIdDesignado?: string; // ID de participante de la sesión o 'TERCERO'
  nombreRazonSocial: string;
  tipoDocumento: TipoDocumentoIdentidad;
  numeroDocumento: string;
  correoAceptacion: string;
  detalleCoproductores?: string;
  coproductores?: CoproductorFonograficoItem[];
  firmaProductor?: FirmaElectronicaRegistro;
}

export interface ContratoSplitEntity {
  id: string;
  codigoSesion?: string;
  fechaHoraSesion?: string;
  estudioSede?: string;
  archivoGrabacionId?: string;
  versionProvisional?: string;
  tipoContrato: TipoContratoSplit;
  tituloObra: string;
  tituloMaster?: string; // Usado como Versión / título provisional
  estado: EstadoContratoSplit;
  fechaVigencia: string;
  jurisdiccion: string;
  ciudad: string;
  notas?: string;
  productorFonografico?: DesignacionProductorFonografico;
  markdownCompilado?: string;
  archivoFirmado?: ArchivoFirmadoMetadata;
  archivosFirmadosHistorico?: ArchivoFirmadoMetadata[];
  bloqueadoEdicion?: boolean;
  fechaBloqueoEdicion?: string;
  motivoBloqueoEdicion?: string;
  creadoEn: string;
  actualizadoEn: string;
  eliminado?: boolean;
}

export interface ColaboradorSplitEntity {
  id: string;
  contratoId: string;
  orden: number;
  nombreCompleto: string;
  tipoDocumento: TipoDocumentoIdentidad;
  numeroDocumento: string;
  nombreArtistico?: string;
  email: string;
  telefono?: string;
  rolContribucion: RolContribucionAutoral;
  aporteCreativo?: string;
  porcentajeSplit: number; // 0 a 100
  ipi?: string; // Opcional
  pro?: string;
  direccion?: string;
  comuna?: string;
  // Sección 3: Indicador rápido si este participante es el productor fonográfico único
  esProductorFonografico?: boolean;
  // Sección 4: Autorizaciones individuales de participantes grabados
  intervencionFijada?: RespuestaSiNo;
  autorizaFijacion?: RespuestaAutorizacionFijacion;
  // Sección 5: Aceptación y firma electrónica directa en plataforma
  firma?: FirmaElectronicaRegistro;
  eliminado?: boolean;
}

/**
 * Mapeo oficial de datos básicos del participante para importación CSV,
 * pre-llenado por cédula (editable) y trazabilidad de producciones.
 */
export interface ParticipanteDirectorioEntity {
  id: string; // Clave normalizada por número de documento
  tipoDocumento: TipoDocumentoIdentidad;
  numeroDocumento: string;
  nombreCompleto: string;
  nombreArtistico?: string;
  email: string;
  telefono?: string;
  rolSugerido?: RolContribucionAutoral;
  aporteSugerido?: string;
  ipi?: string;
  obraReferenciaCsv?: string;
  origen: 'CSV' | 'EXPEDIENTE' | 'MEDELLIN_JOVEN' | 'ASISTENCIA_MML';
  fechaImportacion: string;
  actualizadoEn: string;
}

export interface ProduccionRelacionadaParticipante {
  contratoId: string;
  codigoSesion: string;
  tituloObra: string;
  versionProvisional?: string;
  fechaVigencia: string;
  estudioSede: string;
  estado: EstadoContratoSplit;
  rolEnObra: RolContribucionAutoral;
  aporteCreativo?: string;
  porcentajeAutoria: number;
  esProductorFonografico: boolean;
  firmadoPorParticipante: boolean;
  tieneArchivoFirmadoCustodia: boolean;
}

export type EstadoRelacionSplits = 'SIN_SPLITS' | 'UN_SPLIT' | 'VARIOS_SPLITS';

export interface TrazabilidadParticipanteItem {
  participante: ParticipanteDirectorioEntity;
  producciones: ProduccionRelacionadaParticipante[];
  cantidadProducciones: number;
  estadoRelacion: EstadoRelacionSplits;
}

export interface ResultadoImportacionCsvSplits {
  nombreArchivo: string;
  totalFilasLeidas: number;
  nuevosInsertados: number;
  actualizados: number;
  filasOmitidas?: number;
  conVariosSplits: number;
  conUnSplit: number;
  sinSplits: number;
  errores: string[];
  timestamp: string;
}

export interface EditoraSplitEntity {
  id: string;
  contratoId: string;
  colaboradorId: string;
  nombreRazonSocial: string;
  tipoDocumento: TipoDocumentoIdentidad;
  nit: string;
  email: string;
  porcentaje: number;
  ipi?: string;
  esEditoraPropia: boolean;
  eliminado?: boolean;
}

export interface CondicionesEconomicasEntity {
  id: string;
  contratoId: string;
  moneda: string;
  eliminado?: boolean;
}

export interface VersionSplitEntity {
  id: string;
  contratoId: string;
  versionNumero: number;
  markdownSnapshot: string;
  autorCambio: string;
  motivo: string;
  archivoUrl?: string;
  storageKey?: string;
  nombreArchivo?: string;
  tamanoBytes?: number;
  pendienteSubidaS3?: boolean;
  cambios?: CambioCampoSplit[];
  creadoEn: string;
}

export type TipoEventoTrazabilidad = 
  | 'CREACION'
  | 'ACTUALIZACION'
  | 'MODIFICACION_ACUERDO'
  | 'FIRMA_ELECTRONICA'
  | 'DESCARGA_PDF'
  | 'SUBIDA_FIRMADO'
  | 'ELIMINACION_FIRMADO'
  | 'CAMBIO_ESTADO'
  | 'BLOQUEO_EDICION'
  | 'DESBLOQUEO_EDICION';

export interface EventoTrazabilidadMetadata {
  archivoUrl?: string;
  storageKey?: string;
  nombreArchivo?: string;
  tamanoBytes?: number;
  archivoAnteriorUrl?: string;
  nombreArchivoAnterior?: string;
  versionNumero?: number;
  motivo?: string;
  cambios?: CambioCampoSplit[];
  pendienteSubidaS3?: boolean;
  [key: string]: any;
}

export interface EventoTrazabilidadEntity {
  id: string;
  contratoId: string;
  tipoEvento: TipoEventoTrazabilidad;
  tituloEvento: string;
  descripcion: string;
  usuario: string;
  metadata?: EventoTrazabilidadMetadata;
  timestamp: string;
}

/**
 * Agregado de Dominio Completo
 */
export interface ContratoSplitCompleto {
  contrato: ContratoSplitEntity;
  colaboradores: ColaboradorSplitEntity[];
  editoras: EditoraSplitEntity[];
  condicionesEconomicas: CondicionesEconomicasEntity;
  versiones?: VersionSplitEntity[];
  eventosTrazabilidad?: EventoTrazabilidadEntity[];
}

export interface BalanceSplitsDiagnostic {
  esValido: boolean;
  sumaTotal: number;
  diferencia: number;
  mensaje: string;
  estado: 'DEFICIT' | 'EXCESO' | 'EXACTO';
}

export interface RequisitoEvaluacionItem {
  id: string;
  paso: number; // 1, 2, 3, 4, 5
  titulo: string;
  descripcion: string;
  cumplido: boolean;
  mensajeError?: string;
  tabColaborador?: number;
  esBalanceSplit?: boolean;
}

export interface FaseEvaluacionExpediente {
  paso: number;
  nombre: string;
  subtitulo: string;
  icono: string;
  items: RequisitoEvaluacionItem[];
  ok: number;
  tot: number;
  esValido: boolean;
}

export interface EvaluacionExpedienteSplits {
  fases: FaseEvaluacionExpediente[];
  totalOk: number;
  totalItems: number;
  porcentaje: number;
  esDescargaHabilitada: boolean;
  motivoBloqueo?: string;
}

/**
 * Normaliza cualquier rol heredado o de CSV a uno de los 4 roles oficiales:
 * 'Autor' | 'Compositor' | 'Productor musical' | 'Músico'
 */
export function normalizarRolSplitOficial(rol?: string): RolContribucionAutoral {
  if (!rol) return 'Autor';
  const limpio = rol.trim();
  if (limpio === 'Autor' || limpio === 'Compositor' || limpio === 'Productor musical' || limpio === 'Músico') {
    return limpio;
  }
  const lower = limpio.toLowerCase();
  if (lower.includes('productor') || lower.includes('beat')) return 'Productor musical';
  if (lower.includes('músico') || lower.includes('musico') || lower.includes('arreglista') || lower.includes('interprete') || lower.includes('intérprete')) return 'Músico';
  if (lower === 'musica' || lower === 'música' || lower.includes('compositor')) return 'Compositor';
  return 'Autor';
}

/**
 * Normaliza un tipo de documento proveniente de CSV o entrada manual
 */
export function normalizarTipoDocumento(tipo?: string): TipoDocumentoIdentidad {
  if (!tipo) return 'CC';
  const up = tipo.trim().toUpperCase().replace(/\./g, '');
  if (up === 'TI' || up.includes('TARJETA')) return 'TI';
  if (up === 'CE' || up.includes('EXTRANJERIA') || up.includes('EXTRANJERÍA')) return 'CE';
  if (up === 'PASAPORTE' || up === 'PAS' || up === 'PP' || up.includes('PASAPORTE')) return 'PASAPORTE';
  if (up === 'NIT' || up.includes('TRIBUTARIA')) return 'NIT';
  return 'CC';
}

/**
 * Limpia el número de documento para cruces exactos por cédula/NIT
 */
export function normalizarNumeroDocumento(doc?: string): string {
  if (!doc) return '';
  return doc.trim().replace(/[\s.\-]/g, '').toUpperCase();
}

/**
 * Retorna la fecha y hora local actual en formato YYYY-MM-DD HH:mm
 */
export function obtenerFechaHoraActualFormateada(): string {
  const d = new Date();
  const anio = d.getFullYear();
  const mes = String(d.getMonth() + 1).padStart(2, '0');
  const dia = String(d.getDate()).padStart(2, '0');
  const horas = String(d.getHours()).padStart(2, '0');
  const minutos = String(d.getMinutes()).padStart(2, '0');
  return `${anio}-${mes}-${dia} ${horas}:${minutos}`;
}

/**
 * Genera el código único de sesión consecutivo sin el prefijo "EPG-"
 * Formato: MML-YYYY-XXXX (ej. MML-2026-0001, MML-2026-0002...)
 * Calcula el consecutivo según la cantidad de Split Sheets creados.
 */
export function generarCodigoSesionConsecutivo(
  contratosExistentes: ReadonlyArray<{ id?: string; codigoSesion?: string }> = []
): string {
  const anio = new Date().getFullYear();
  const prefijo = `MML-${anio}-`;
  const codigosUsados = new Set<string>();
  let maxConsecutivoSecuencial = 0;

  for (const c of contratosExistentes || []) {
    const cod = (c.codigoSesion || c.id || '').trim().toUpperCase();
    if (!cod) continue;
    codigosUsados.add(cod);
    const sinEpg = cod.replace(/^EPG-/, '');
    codigosUsados.add(sinEpg);

    const match = sinEpg.match(/^MML-\d{4}-(\d+)$/);
    if (match) {
      const num = parseInt(match[1], 10);
      if (!isNaN(num) && num > 0 && num < 1000 && num > maxConsecutivoSecuencial) {
        maxConsecutivoSecuencial = num;
      }
    }
  }

  let siguienteNumero = Math.max((contratosExistentes || []).length + 1, maxConsecutivoSecuencial + 1);
  let candidato = `${prefijo}${String(siguienteNumero).padStart(4, '0')}`;

  while (codigosUsados.has(candidato.toUpperCase())) {
    siguienteNumero++;
    candidato = `${prefijo}${String(siguienteNumero).padStart(4, '0')}`;
  }

  return candidato;
}
